import type { TenantSegmentDto } from '@/services/generated/api';

/**
 * How an organization issues its tax documents today — screen 4 of
 * `docs/DIGITAL_ONBOARDING.md`, and what decides which steps «Empezar aquí»
 * shows at all.
 *
 * The vocabulary is the backend's (`src/modules/tenants/segment.ts`, a CHECK
 * constraint in SQL); what lives here is only what a person reads.
 */
export type TenantSegment = NonNullable<TenantSegmentDto['segment']>;

export interface SegmentOption {
  value: TenantSegment;
  label: string;
  /** One line on what this answer means for them, not on what it means for us. */
  hint: string;
}

/**
 * In the order they are offered, which is not alphabetical and not arbitrary:
 * the first two are the two largest pools, and «con el sistema gratuito del
 * SII» is second rather than first because somebody who has never issued at all
 * should not have to read past a description they will not recognise.
 */
export const SEGMENT_OPTIONS: readonly SegmentOption[] = [
  {
    value: 'not_issuing',
    label: 'Todavía no emito',
    hint: 'Partimos por el certificado digital, que es el paso que más demora.',
  },
  {
    value: 'sii_free_system',
    label: 'Con el sistema de facturación gratuito del SII',
    hint: 'Ya tienes certificado y folios: podemos traer los documentos que ya emitiste.',
  },
  {
    value: 'other_provider',
    label: 'Con otro proveedor',
    hint: 'Ya estás autorizado. Vamos directo a la migración y a conectar tu ERP.',
  },
  {
    value: 'accountant',
    label: 'Llevo la contabilidad de varias empresas',
    hint: 'Te dejamos varias empresas desde el principio.',
  },
  {
    value: 'api_integration',
    label: 'Vengo a integrar por API',
    hint: 'Sandbox, llaves y la colección de Postman. Sin asistente.',
  },
];
